import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

const motivLabels = {
  neplata: "neplata chiriei",
  reparatii: "neefectuarea reparatiilor necesare de catre proprietar",
  denuntare: "denuntarea unilaterala a contractului, cu respectarea termenului de preaviz",
};

const NotificarePreview = forwardRef(function NotificarePreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());
  const expeditorTip = data.expeditorEsteChirias ? "Chirias" : "Proprietar";
  const destinatarTip = data.expeditorEsteChirias ? "Proprietar" : "Chirias";
  const ciExpFmt = data.expeditorCI ? formatCI(data.expeditorCI) : "___ ___";

  const preavizZile = data.preaviz === "alt" ? (data.preavizCustom || "___") : (data.preaviz || "___");
  const motivText = data.motivReziliere === "altele"
    ? (data.motivDetalii || "___________________________")
    : (motivLabels[data.motivReziliere] || "___________________________");

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 12px 0",
    textAlign: "justify",
  };

  const bold = { fontWeight: 700, color: "#2d2418" };
  const fieldRow = { ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" };

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "22px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}>
          Notificare de Reziliere
        </h2>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "16px",
          fontWeight: 700,
          color: "#4a3f35",
          margin: "4px 0 0 0",
        }}>
          Contract de Inchiriere
        </p>
        <p style={{
          fontSize: "13px",
          fontFamily: "'Georgia', serif",
          color: "#8a7e70",
          margin: "8px 0 0 0",
        }}>
          Data: {data.dataNotificare ? fmtDateFromISO(data.dataNotificare) : today}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* Expeditor */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={p}><span style={bold}>Expeditor ({expeditorTip}):</span></p>
        <p style={fieldRow}>Nume: <span style={bold}>{data.expeditorNume || "________________________"}</span></p>
        <p style={fieldRow}>CNP: <span style={bold}>{data.expeditorCNP || "________________________"}</span></p>
        <p style={fieldRow}>CI seria <span style={bold}>{ciExpFmt}</span></p>
        <p style={fieldRow}>Domiciliul: <span style={bold}>{data.expeditorAdresa || "________________________"}</span></p>
      </div>

      {/* Destinatar */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "24px" }}>
        <p style={p}><span style={bold}>Destinatar ({destinatarTip}):</span></p>
        <p style={fieldRow}>Nume: <span style={bold}>{data.destinatarNume || "________________________"}</span></p>
        <p style={fieldRow}>Adresa: <span style={bold}>{data.destinatarAdresa || "________________________"}</span></p>
      </div>

      <hr style={{ border: "none", borderTop: "1px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* Body */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
        <p style={p}>
          Subsemnatul/a, <span style={bold}>{data.expeditorNume || "________________________"}</span>, in calitate de <span style={bold}>{expeditorTip.toLowerCase()}</span> al imobilului situat in <span style={bold}>{data.contractAdresaImobil || "________________________"}</span>, in temeiul contractului de inchiriere nr. <span style={bold}>{data.contractNumar || "___"}</span> din data de <span style={bold}>{data.contractData ? fmtDateFromISO(data.contractData) : "___________"}</span>,
        </p>

        <p style={p}>
          va aduc la cunostinta prin prezenta notificare decizia mea de <span style={bold}>reziliere a contractului de inchiriere</span> mentionat mai sus, avand ca motiv: <span style={bold}>{motivText}</span>.
        </p>

        <p style={p}>
          In conformitate cu prevederile contractuale si ale Codului Civil, va acord un termen de preaviz de <span style={bold}>{preavizZile} zile</span>.
        </p>

        <p style={p}>
          Prin urmare, contractul de inchiriere va inceta sa produca efecte incepand cu data de <span style={bold}>{data.dataIncetare ? fmtDateFromISO(data.dataIncetare) : "___________"}</span>.
        </p>

        <p style={p}>
          Va rog sa luati act de prezenta notificare si sa procedati la {data.expeditorEsteChirias
            ? "predarea imobilului conform conditiilor contractuale, inclusiv eliberarea imobilului si restituirea cheilor la data mentionata mai sus."
            : "eliberarea imobilului si predarea cheilor la data mentionata mai sus, conform conditiilor contractuale."
          }
        </p>

        <p style={p}>
          De asemenea, va rog sa aveti in vedere urmatoarele:
        </p>
        <p style={{ ...p, paddingLeft: isMobile ? "12px" : "20px" }}>
          - Regularizarea tuturor obligatiilor financiare restante (chirii, utilitati){"\n"}
          - Intocmirea procesului-verbal de predare-primire a imobilului{"\n"}
          - Restituirea garantiei, dupa caz, conform prevederilor contractuale
        </p>

        <p style={p}>
          Prezenta notificare a fost intocmita in doua exemplare si transmisa prin scrisoare recomandata cu confirmare de primire / inmanata personal sub semnatura.
        </p>
      </div>

      {/* Signature */}
      <div style={{
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        <div style={{ maxWidth: "300px" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {expeditorTip.toUpperCase()}
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 0 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.expeditorNume || ""}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {data.dataNotificare ? fmtDateFromISO(data.dataNotificare) : today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default NotificarePreview;
